import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {LoadingContent} from "../LoadingContent";
import React, {useState, useMemo, useEffect, useCallback, useRef} from "react";
import {Box, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, TextField, Pagination} from "@mui/material";
import GavelIcon from '@mui/icons-material/Gavel';
import {DivisionBan} from "../../data/DivisionBan";
import {formatDate} from "../../utils";
import IssueBanForm from "./IssueBanForm";
import {ConfirmationDialog} from "../ConfirmationDialog";
import {PoliceUser} from "../PoliceUser";

interface DivisionBanPaginatedResponse {
    totalCount: number
    totalPages: number
    bans: DivisionBan[]
}

interface BanListProps {
    divisionIdentifier: string
}

export interface BanListRowProps extends BanListProps {
    ban: DivisionBan
    onUnban: () => void
}

const BanListRow = (props: BanListRowProps) => {
    const [openConfirm, setOpenConfirm] = useState(false)

    const unban = (confirm: boolean) => {
        setOpenConfirm(false)
        if (confirm) {
            makeApiCall({
                url: `/api/division/${props.divisionIdentifier}/ban`,
                method: "PATCH",
                body: {
                    banId: props.ban.id,
                    banned: true
                },
                onLoadedCallback: () => {
                    props.onUnban()
                },
                onError: () => {}
            })
        }
    }

    let banTime
    if (props.ban.isPermanent && !props.ban.unbanned) {
        banTime = <TableCell sx={{backgroundColor: "darkred", color: "white"}}>Permanent</TableCell>
    } else if (!props.ban.expired && !props.ban.unbanned) {
        banTime = <TableCell sx={{backgroundColor: "darkgoldenrod", color: "white"}}>{formatDate(props.ban.endDate)}</TableCell>
    } else if (props.ban.unbanned) {
        banTime = <TableCell sx={{backgroundColor: "darkgreen", color: "white"}}>{formatDate(props.ban.endDate)} (Unbanned)</TableCell>
    } else {
        banTime = <TableCell sx={{backgroundColor: "darkgreen", color: "white"}}>{formatDate(props.ban.endDate)} (Expired)</TableCell>
    }

    return <TableRow>
        <TableCell>
        <PoliceUser user={{nick: props.ban.supervisorNick!, communityId: props.ban.supervisorCommunityId}}/>
        </TableCell>
        <TableCell>
        <PoliceUser user={{nick: props.ban.employeeNick, communityId: props.ban.employeeCommunityId}}/>
        </TableCell>
        <TableCell>{props.ban.reason}</TableCell>
        <TableCell>
            {!props.ban.unbanned && !props.ban.expired ? <Tooltip title={"Unban"}>
                <IconButton size="small" color="success" onClick={() =>
                    setOpenConfirm(true)
                }>
                    <GavelIcon color={"success"} fontSize="small" />
                </IconButton>
            </Tooltip> : <></> }
        </TableCell>
        {banTime}
        <ConfirmationDialog title={"Unban User"} description={"This will unban the user, this is not undoable"} open={openConfirm} onClose={unban} />
    </TableRow>
}

export const BanList = (props: BanListProps) => {
    const [query, setQuery] = useState("")
    const [page, setPage] = useState(1)
    const [debouncedQuery, setDebouncedQuery] = useState("")
    const debounceTimer = useRef<NodeJS.Timeout | null>(null)

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value)
        if (debounceTimer.current) clearTimeout(debounceTimer.current)
        debounceTimer.current = setTimeout(() => {
            setDebouncedQuery(value)
            setPage(1)
        }, 400)
    }, [])

    useEffect(() => {
        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current) }
    }, [])

    const url = useMemo(() => {
        const params = new URLSearchParams()
        params.set("page", String(page))
        if (debouncedQuery) params.set("query", debouncedQuery)
        return `/api/division/${props.divisionIdentifier}/ban?${params.toString()}`
    }, [page, debouncedQuery, props.divisionIdentifier])

    const data = useApiCall<DivisionBanPaginatedResponse>({url})
    const totalPages = Math.max(1, data.data?.totalPages ?? 1)

    return <Stack spacing={4}>
            <TextField
                fullWidth
                variant="standard"
                label="Search by name or community ID..."
                value={query}
                onChange={(e) => handleQueryChange(e.target.value)}
            />
            <IssueBanForm divisionIdentifier={props.divisionIdentifier} onBanIssued={data.refresh} />
            <Box sx={{overflowX: "auto"}}>
            <LoadingContent showLoadingSkeleton isLoading={data.isLoading}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Issuer</TableCell>
                            <TableCell>Officer</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Actions</TableCell>
                            <TableCell>End Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.data?.bans?.map((bans) =>
                            <BanListRow key={bans.id} ban={bans} divisionIdentifier={props.divisionIdentifier} onUnban={data.refresh}/>
                        )}
                    </TableBody>
                </Table>
            </LoadingContent>
            </Box>
            <Box sx={{display: "flex", justifyContent: "center"}}>
                <Pagination color="secondary" count={totalPages} page={page} onChange={(_, v) => setPage(v)}/>
            </Box>
        </Stack>
}